import {difference} from 'lodash';
import React, {FC, useCallback, useMemo, useRef} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {TopBarDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/topBarDataEventObject';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {markAdminSuggestionsAsSeenAction} from '../../../../../libs/shared/core/src/store/data/stash/stashActions';
import {getSeenAdminSuggestionTypes} from '../../../../../libs/shared/core/src/store/data/stash/stashSelectors';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {useFetchAdminSuggestions} from '../../../../settings/suggestions/adminSuggestionHooks';
import {AdminSuggestionCardSourcesEnum} from '../../../../settings/suggestions/adminSuggestionTypes';
import {AppState} from '../../../../store/appState';
import {navigateTopBarPopoverAction} from '../../../../store/panels/app/appPanelActions';
import {makeGetIsHelpPopoverVisible} from '../../../../store/panels/app/appPanelSelectors';
import {AppPanelPopoverTypesEnum} from '../../../../store/panels/app/appPanelState';
import {TopBarButton} from '../topBarButton';
import {useFetchProductUpdatesPeriodically} from './productUpdatesHelpers';
import {SettingsHelpWidget} from './settingsHelpWidget';

/*
 * Constants.
 */

interface HelpProps {
  panelId: AppPanelId;
}

/*
 * Style.
 */

const StyledTopBarButton = styled(TopBarButton)`
  /* 
   * The helpIndicator icon (P0) differs from most icons in that we want the icon's indicator to have a different
   * color than the fill. So we hand-off the color using a CSS variable.
   */
  --theme-palette-blue-shade40: ${(p) => p.theme.palette.blue.shade40};
`;

/*
 * Component.
 */

export const Help: FC<HelpProps> = ({panelId}) => {
  const buttonRef = useRef<HTMLDivElement>(null);

  const {trackDataEvent} = useDataEventTracking();

  const getIsHelpPopoverVisible = useMemo(() => makeGetIsHelpPopoverVisible(), []);
  const isPopoverVisible = useAppSelector((state: AppState) => getIsHelpPopoverVisible(state, {panelId}));

  const dispatch = useAppDispatch();

  const adminSuggestions = useFetchAdminSuggestions({
    source: {type: AdminSuggestionCardSourcesEnum.HELP_WIDGET},
  });

  const {productUpdates, hasUnseenProductUpdates, onProductUpdatesSeen} =
    useFetchProductUpdatesPeriodically();

  const helpLabel = <Trans id="self-serve-help-button">Help</Trans>;

  const onHelpClick = () => {
    trackDataEvent(DataEventActionsEnum.CLICK, TopBarDataEventObject.HELP_WIDGET_OPEN);
    dispatch(navigateTopBarPopoverAction(panelId, AppPanelPopoverTypesEnum.HELP));
  };

  const onRequestClose = () => {
    dispatch(navigateTopBarPopoverAction(panelId, undefined));
  };

  const seenAdminSuggestionTypes = useAppSelector(getSeenAdminSuggestionTypes);

  const hasUnseenAdminSuggestions = useMemo(() => {
    const types = adminSuggestions.map((s) => s.type);

    return difference(types, seenAdminSuggestionTypes).length > 0;
  }, [adminSuggestions, seenAdminSuggestionTypes]);

  const onAdminSuggestionsSeen = useCallback(() => {
    if (!hasUnseenAdminSuggestions) {
      return;
    }

    const types = adminSuggestions.map((s) => s.type);
    dispatch(markAdminSuggestionsAsSeenAction(types));
  }, [adminSuggestions, dispatch, hasUnseenAdminSuggestions]);

  const hasPill = hasUnseenProductUpdates || hasUnseenAdminSuggestions;

  return (
    <>
      <StyledTopBarButton
        ref={buttonRef}
        iconName={hasPill ? 'helpIndicator' : 'help'}
        isSelected={isPopoverVisible}
        renderTooltip={() => helpLabel}
        interactionId="p0-settings-help"
        onClick={onHelpClick}
      >
        {helpLabel}
      </StyledTopBarButton>
      {isPopoverVisible && (
        <SettingsHelpWidget
          panelId={panelId}
          adminSuggestions={adminSuggestions ?? []}
          hasUnseenAdminSuggestions={hasUnseenAdminSuggestions}
          onAdminSuggestionsSeen={onAdminSuggestionsSeen}
          productUpdates={productUpdates}
          hasUnseenProductUpdates={hasUnseenProductUpdates}
          onProductUpdatesSeen={onProductUpdatesSeen}
          anchor={buttonRef}
          onOpenClick={onOpenClick}
          onRequestClose={onRequestClose}
        />
      )}
    </>
  );
};

/*
 * Helpers.
 */

const onOpenClick = () => {
  trackInterfaceEvent(InterfaceEventsEnum.HELP_WIDGET_OPEN_CLICK);
};
